// src/pages/Orders/DriverPage.jsx
// Driver-facing report (under Get Order on Map). The logged-in driver sees a map
// on top with the orders an admin assigned to them, and the same order rows /
// columns as "Get Orders by Day" underneath. Data source: /assignments/my-orders
// (their assigned order ids) → order details filter (full rows + coordinates).
import React from "react";
import { Link } from "react-router-dom";
import api from "@services/api";
import { useOrderDetailsFilterQuery } from "@queries/order";
import { useCustomersUnpaidBulkQuery } from "@queries/customers";
import { useTimeSlotsQuery } from "@queries/time_slots";
import { PAYMENT_METHODS, paymentInfo, paymentLabel } from "@lib/paymentMethods";
import { loadGoogleMaps } from "@lib/googleMapsLoader";
import { useAuth } from "@context/AuthContext";
import "@styles/opencart-admin.css";

const LAUNDRY_AGENT_GROUP_ID = 14;

const AMMAN = { lat: 31.9539, lng: 35.9106 };

const isJordanLat = (v) => v >= 29 && v <= 33.8;
const isJordanLng = (v) => v >= 34.5 && v <= 39.5;

// Decode a MySQL POINT (WKB) as returned in order.pickup_latlong / dropoff_latlong.
// Same logic as the Orders-on-Map page so markers land correctly.
function decodeMysqlPointWkb(value) {
  if (!value) return null;
  const bytes = new Uint8Array([...String(value)].map((ch) => ch.charCodeAt(0) & 0xff));
  const attempts = [0, 4];
  const results = [];
  for (const offset of attempts) {
    if (bytes.length < offset + 1 + 4 + 8 + 8) continue;
    const dv = new DataView(bytes.buffer, bytes.byteOffset + offset, bytes.byteLength - offset);
    const little = dv.getUint8(0) === 1;
    if (dv.getUint32(1, little) !== 1) continue;
    const x = dv.getFloat64(5, little);
    const y = dv.getFloat64(13, little);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const check = (a, b) => {
      if (isJordanLat(a) && isJordanLng(b)) return { lat: a, lng: b, jordan: true };
      if (isJordanLat(b) && isJordanLng(a)) return { lat: b, lng: a, jordan: true };
      const isLat = (v) => v >= -90 && v <= 90;
      const isLng = (v) => v >= -180 && v <= 180;
      if (isLat(a) && isLng(b)) return { lat: a, lng: b, jordan: false };
      if (isLat(b) && isLng(a)) return { lat: b, lng: a, jordan: false };
      return null;
    };
    const parsed = check(y, x) || check(x, y);
    if (parsed) results.push(parsed);
  }
  if (!results.length) return null;
  const pick = results.find((r) => r.jordan) || results.find((r) => Math.abs(r.lat) > 0.0001 || Math.abs(r.lng) > 0.0001) || results[0];
  delete pick.jordan;
  return pick;
}

// Orders sometimes store lat/lng swapped. Jordan is ~lat 29–33.5, lng 34.5–39.5.
// Normalise so the marker lands in the right place.
function normalizeCoords(lat, lng) {
  lat = Number(lat); lng = Number(lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) < 0.01 || Math.abs(lng) < 0.01) return null;
  const inLat = (v) => v >= 29 && v <= 33.5;
  const inLng = (v) => v >= 34.5 && v <= 39.5;
  if (inLat(lat) && inLng(lng)) return { lat, lng };
  if (inLat(lng) && inLng(lat)) return { lat: lng, lng: lat }; // swapped
  return { lat, lng };
}

// Pickup markers use the pickup location, delivery markers the drop-off location.
function coordsForKind(r, kind) {
  if (kind === "pickup") {
    return (
      normalizeCoords(r.pickup_lat, r.pickup_lng) ||
      decodeMysqlPointWkb(r?.order?.pickup_latlong) ||
      normalizeCoords(r.dropoff_lat, r.dropoff_lng) ||
      decodeMysqlPointWkb(r?.order?.dropoff_latlong)
    );
  }
  return (
    normalizeCoords(r.dropoff_lat, r.dropoff_lng) ||
    decodeMysqlPointWkb(r?.order?.dropoff_latlong) ||
    normalizeCoords(r.pickup_lat, r.pickup_lng) ||
    decodeMysqlPointWkb(r?.order?.pickup_latlong)
  );
}

const KIND_COLOR = { pickup: "#f59e0b", dropoff: "#2563eb" }; // orange = pickup, blue = delivery

// The DRIVER'S CURRENT PHYSICAL TASK for this assignment. One driver handles the
// whole order (pickup then delivery), so for the normal (dropoff) assignment the
// task follows the order's live STAGE: they must go to the PICKUP address while
// the order is still at pickup stage (Processing 2 / Pick up 15), and to the
// DROP-OFF address once it has moved on (Cleaning 17 / Delivery 18 / Complete 5).
// A split pickup-only assignment (kind = pickup) is always a pickup.
const PICKUP_STATUSES = new Set([2, 15]);
const legOf = (r) => {
  if (r.kind === "pickup") return "pickup";
  const st = Number(r.order?.order_status_id ?? r.order_status_id);
  return PICKUP_STATUSES.has(st) ? "pickup" : "dropoff";
};

// Is this row's work finished? A pickup-only assignment is done once the order
// has moved past pickup (17/18/5). A normal (whole-order) assignment is done only
// when the order is Complete (5). Done rows are greyed, sent to the bottom,
// dropped from the map, and get no "Next" button.
const PICKUP_DONE_STATUSES = new Set([17, 18, 5]);
const legDone = (r) => {
  const st = Number(r.order?.order_status_id ?? r.order_status_id);
  return r.kind === "pickup" ? PICKUP_DONE_STATUSES.has(st) : st === 5;
};

// Badge label for a finished row.
const doneLabel = (r) => (r.kind === "pickup" ? "Pickup Done ✅" : "Delivered ✅");

// Task-appropriate phone: follows the assigned leg (pickup vs drop-off),
// falling back to the order's main phone.
const phoneOf = (r) =>
  (legOf(r) === "pickup" ? r.order?.pickup_telephone : r.order?.dropoff_telephone) ||
  r.order?.telephone || r.telephone || "";

// Google Maps link for the location of this driver's assigned leg: pickup
// address for a pickup assignment, drop-off address for a delivery assignment.
// Uses the exact coordinates when available, else a text search on the address.
const mapUrlOf = (r) => {
  const stage = legOf(r);
  const pos = coordsForKind(r, stage);
  if (pos) return `https://www.google.com/maps?q=${pos.lat},${pos.lng}`;
  const addr = stage === "pickup" ? r.order?.pickup_address : r.order?.dropoff_address;
  return addr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}` : null;
};

function DriverMap({ rows, onSetPosition }) {
  const mapRef = React.useRef(null);
  const gmapRef = React.useRef(null);
  const markersRef = React.useRef([]);
  const [ready, setReady] = React.useState(false);
  // Keep the latest callback for marker handlers without rebuilding markers.
  const setPosRef = React.useRef(onSetPosition);
  setPosRef.current = onSetPosition;

  React.useEffect(() => {
    let ok = true;
    loadGoogleMaps().then(() => { if (ok) setReady(true); }).catch(() => {});
    return () => { ok = false; };
  }, []);

  React.useEffect(() => {
    if (!ready || !mapRef.current) return;
    if (!gmapRef.current) {
      gmapRef.current = new window.google.maps.Map(mapRef.current, {
        center: AMMAN, zoom: 11, mapTypeControl: false, streetViewControl: false,
      });
    }
    const map = gmapRef.current;
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    const bounds = new window.google.maps.LatLngBounds();
    let count = 0;
    rows.forEach((r, i) => {
      const leg = legOf(r);
      const pos = coordsForKind(r, leg);
      if (!pos) return;
      const color = KIND_COLOR[leg] || "#2563eb";
      const marker = new window.google.maps.Marker({
        position: pos, map,
        icon: {
          path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
          fillColor: color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2,
          scale: 1.9, anchor: new window.google.maps.Point(12, 22), labelOrigin: new window.google.maps.Point(12, 9),
        },
        label: { text: String(i + 1), color: "#fff", fontSize: "11px", fontWeight: "700" },
        title: `${leg === "pickup" ? "Pickup" : "Delivery"} #${r.order_id} — ${r.customer || ""}`,
      });
      const info = new window.google.maps.InfoWindow();
      marker.addListener("click", () => {
        const node = document.createElement("div");
        node.style.font = "13px system-ui";
        node.innerHTML =
          `<b>${leg === "pickup" ? "🟠 Pickup" : "🔵 Delivery"} #${r.order_id}</b> — ${r.customer || ""}<br/>` +
          `${(leg === "pickup" ? r.pickup_time_slot : r.dropoff_time_slot) || ""} · ${r.area || ""}`;
        const wrap = document.createElement("div");
        wrap.style.marginTop = "6px";
        const lbl = document.createElement("span");
        lbl.textContent = "Stop #: ";
        const inp = document.createElement("input");
        inp.type = "number"; inp.min = "1"; inp.value = String(i + 1);
        inp.style.width = "58px"; inp.style.padding = "2px 4px";
        const commit = () => { if (inp.value !== "" && Number(inp.value) !== i + 1) { setPosRef.current?.(r.order_id, r.kind, inp.value); info.close(); } };
        inp.addEventListener("keydown", (e) => { if (e.key === "Enter") commit(); });
        inp.addEventListener("change", commit);
        wrap.appendChild(lbl); wrap.appendChild(inp);
        node.appendChild(wrap);
        info.setContent(node);
        info.open(map, marker);
      });
      markersRef.current.push(marker);
      bounds.extend(pos); count++;
    });
    if (count > 0) { map.fitBounds(bounds); if (count === 1) map.setZoom(14); }
  }, [ready, rows]);

  return (
    <div>
      <div style={{ display: "flex", gap: 16, padding: "0 2px 8px", fontSize: 12, color: "#374151" }}>
        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 10, background: KIND_COLOR.pickup, marginRight: 5 }} />Pickup</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 10, background: KIND_COLOR.dropoff, marginRight: 5 }} />Delivery</span>
      </div>
      <div ref={mapRef} style={{ width: "100%", height: 380, borderRadius: 10, background: "#eef2f7" }} />
    </div>
  );
}

const fmtTotal = (t) => (t === "F.O.C" ? "F.O.C" : Number(t || 0).toFixed(2));
const stripHtml = (s) =>
  String(s || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Readable label for the stored payment_method code (collection statement groups).
const PAY_LABEL = {
  cod: "Cash on delivery",
  ccod: "Card on delivery",
  cc: "Credit card (paid online)",
  cliq: "CliQ",
  bank_transfer: "Bank transfer",
  "cash on delivery": "Cash on delivery",
  payfort: "Credit card (paid online)",
  "": "Not set",
};
// Known codes keep the collection-report wording above; anything else (English
// full labels in any casing, Arabic labels, "PayFort", …) is canonicalized
// through the shared helper so it never shows a raw code. (WN-0217)
const payLabel = (p) => PAY_LABEL[String(p || "").toLowerCase()] || paymentInfo(p).label;
const numTotal = (t) => (t === "F.O.C" || t == null ? 0 : Number(t) || 0);
const isFoc = (r) => r.order_total === "F.O.C" || String(r.order?.complemantary_reason || "").trim() !== "";

export default function DriverPage() {
  const { user } = useAuth();
  const isDriver = Number(user?.user_group_id) === LAUNDRY_AGENT_GROUP_ID;

  // Today in Amman (YYYY-MM-DD). NOTE: the date filter defaults to EMPTY (show ALL
  // of the driver's assigned orders) — defaulting to today hid every assignment
  // not dated today and made the page look empty ("orders stopped showing"). The
  // ✕ button jumps to today; clearing the field shows all again.
  const ammanToday = React.useMemo(
    () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Amman" }),
    []
  );
  const [type, setType] = React.useState("both");
  const [date, setDate] = React.useState(""); // "" = all assigned dates (never hide orders)
  const [slotFilter, setSlotFilter] = React.useState(""); // "" = All timeslots
  // assignments = [{ order_id, type: 'pickup'|'dropoff' }] — one per assigned task.
  const [assignments, setAssignments] = React.useState(null); // null=loading
  const [loadingIds, setLoadingIds] = React.useState(!isDriver ? false : true);
  const [err, setErr] = React.useState(null);

  // Admins/marketplace admins pick a driver to view; a driver views their own.
  const [drivers, setDrivers] = React.useState([]);
  const [driverId, setDriverId] = React.useState("");
  React.useEffect(() => {
    if (isDriver) return;
    api.get("/assignments/drivers").then((r) => setDrivers(r.data?.data || [])).catch(() => {});
  }, [isDriver]);

  const loadMine = React.useCallback(() => {
    // For an admin, wait until a driver is chosen.
    if (!isDriver && !driverId) { setAssignments([]); return; }
    setLoadingIds(true); setErr(null);
    const params = { type }; // 'both' | 'pickup' | 'dropoff'
    if (date) params.date = date;
    if (!isDriver && driverId) params.driver_id = driverId;
    api.get("/assignments/my-orders", { params })
      .then((r) => setAssignments((r.data?.orders || []).map((o) => ({ order_id: o.order_id, type: o.type }))))
      .catch((e) => setErr(e?.response?.data?.message || "Failed to load orders."))
      .finally(() => setLoadingIds(false));
  }, [type, date, isDriver, driverId]);
  React.useEffect(() => { loadMine(); }, [loadMine]);

  const uniqueIds = React.useMemo(
    () => [...new Set((assignments || []).map((a) => a.order_id))],
    [assignments]
  );
  const hasIds = uniqueIds.length > 0;
  const { data, isFetching, refetch: refetchDetails } = useOrderDetailsFilterQuery(
    hasIds ? { order_ids: uniqueIds, franchise_id: "all" } : {},
    { respectMarketplace: false }
  );

  // Advance an order to its next status — reuses the SAME endpoint the order
  // page uses (POST /order/{id}/status), so status logic lives in one place.
  const [advancing, setAdvancing] = React.useState(null); // order_id being advanced
  const advanceStatus = React.useCallback((r) => {
    if (!r?.next_order_status_id) return;
    if (!window.confirm(`Move order #${r.order_id} to "${r.next_status_text}"?`)) return;
    setAdvancing(r.order_id);
    api.post(`/order/${r.order_id}/status`, {
      status: r.next_order_status_id, comment: "", notify: 1, override: false,
    })
      .then(() => refetchDetails())
      .catch((e) => alert(e?.response?.data?.message || "Failed to change status."))
      .finally(() => setAdvancing(null));
  }, [refetchDetails]);

  // Editable payment method (WN-0162): save instantly, show a ✓ tick, log on the
  // backend. `payOverride` keeps the just-saved value visible until the refetch.
  const [payOverride, setPayOverride] = React.useState({}); // { [order_id]: code }
  const [paySaved, setPaySaved] = React.useState(null);     // order_id showing ✓
  const [paySaving, setPaySaving] = React.useState(null);
  const savePayment = React.useCallback((r, value) => {
    setPaySaving(r.order_id);
    api.post(`/order/${r.order_id}/payment-method`, { payment_method: value })
      .then(() => {
        setPayOverride((m) => ({ ...m, [r.order_id]: value }));
        setPaySaved(r.order_id);
        setTimeout(() => setPaySaved((id) => (id === r.order_id ? null : id)), 1500);
        refetchDetails();
      })
      .catch((e) => alert(e?.response?.data?.message || "Failed to change payment method."))
      .finally(() => setPaySaving((id) => (id === r.order_id ? null : id)));
  }, [refetchDetails]);

  // Driver-side Collection Report modal (WN-0165): collect a customer's unpaid
  // orders at delivery — mark them paid + Complete in one atomic call.
  const [collectRow, setCollectRow] = React.useState(null);   // the row that opened it
  const [collectList, setCollectList] = React.useState(null); // {total, orders} | null=loading
  const [collectSel, setCollectSel] = React.useState(() => new Set());
  const [collectMethod, setCollectMethod] = React.useState("cod");
  const [collectFile, setCollectFile] = React.useState(null);
  const [collectBusy, setCollectBusy] = React.useState(false);
  const openCollect = React.useCallback((r) => {
    const cid = r.order?.customer_id;
    if (!cid) return;
    setCollectRow(r); setCollectList(null); setCollectSel(new Set());
    setCollectMethod(r.payment ? paymentInfo(r.payment).code : "cod"); setCollectFile(null);
    api.get(`/customers/${cid}/unpaid-invoices`)
      .then((res) => {
        const d = res.data?.data || { total: 0, orders: [] };
        const counted = (d.orders || []).filter((o) => o.counted);
        setCollectList({ total: d.total, orders: counted });
        setCollectSel(new Set(counted.map((o) => o.order_id))); // default: all selected
      })
      .catch(() => setCollectList({ total: 0, orders: [] }));
  }, []);
  const collectTotal = React.useMemo(() => {
    if (!collectList) return 0;
    return collectList.orders.filter((o) => collectSel.has(o.order_id)).reduce((s, o) => s + Number(o.total || 0), 0);
  }, [collectList, collectSel]);
  const confirmCollect = React.useCallback(() => {
    if (collectBusy || !collectList) return;
    const ids = [...collectSel];
    if (ids.length === 0) { alert("Select at least one order."); return; }
    if (collectMethod === "cliq" && !collectFile) { alert("A transfer screenshot is required for CliQ."); return; }
    const label = paymentLabel(collectMethod);
    if (!window.confirm(`Collect ${ids.length} order(s) totaling ${collectTotal.toFixed(2)} JOD via ${label}. Confirm?`)) return;
    setCollectBusy(true);
    const fd = new FormData();
    ids.forEach((id) => fd.append("order_ids[]", id));
    fd.append("payment_method", collectMethod);
    if (collectFile) fd.append("image", collectFile);
    api.post(`/orders/collect`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((res) => {
        const n = res.data?.count ?? ids.length;
        alert(`Collected ${n} order(s) via ${label}.`);
        setCollectRow(null); setCollectList(null);
        refetchDetails();
      })
      .catch((e) => alert(e?.response?.data?.message || "Collection failed."))
      .finally(() => setCollectBusy(false));
  }, [collectBusy, collectList, collectSel, collectMethod, collectFile, collectTotal, refetchDetails]);

  // Attach payment-proof image (WN-0163).
  const [attachOverride, setAttachOverride] = React.useState({}); // { [order_id]: count }
  const [uploadingImg, setUploadingImg] = React.useState(null);
  const attachImage = React.useCallback((r, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("image", file);
    setUploadingImg(r.order_id);
    api.post(`/order/${r.order_id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((res) => {
        setAttachOverride((m) => ({ ...m, [r.order_id]: res.data?.attachment_count ?? ((attachOverride[r.order_id] ?? r.attachment_count ?? 0) + 1) }));
        refetchDetails();
      })
      .catch((e) => alert(e?.response?.data?.message || "Failed to upload image."))
      .finally(() => setUploadingImg((id) => (id === r.order_id ? null : id)));
  }, [refetchDetails, attachOverride]);

  // Driver-entered collected amount (paid more/less than the order value).
  const [editCollected, setEditCollected] = React.useState(null); // order_id being edited
  const [collectedVal, setCollectedVal] = React.useState("");
  const saveCollected = React.useCallback((r) => {
    const amount = collectedVal.trim() === "" ? null : Number(collectedVal);
    api.post(`/order/${r.order_id}/collected`, { amount })
      .then(() => { setEditCollected(null); refetchDetails(); })
      .catch((e) => alert(e?.response?.data?.message || "Failed to save collected amount."));
  }, [collectedVal, refetchDetails]);

  // One display row per assigned task (an order assigned for BOTH pickup and
  // delivery shows twice — once per kind), joined to its full order details.
  const rows = React.useMemo(() => {
    if (!hasIds) return [];
    const byId = {};
    for (const d of (data?.data || [])) byId[d.order_id] = d;
    const base = (assignments || [])
      .map((a) => (byId[a.order_id] ? { ...byId[a.order_id], kind: a.type } : null))
      .filter(Boolean);
    // Completed legs sink to the bottom (still visible, greyed) — active stops
    // stay on top in their assigned order. Stable in modern engines.
    return base.sort((a, b) => (legDone(a) ? 1 : 0) - (legDone(b) ? 1 : 0));
  }, [assignments, data, hasIds]);

  // Timeslot filter options (WN-0161) — the system's active slots.
  const { data: slotOptions = [] } = useTimeSlotsQuery(date || undefined);
  const rowSlot = (r) => (legOf(r) === "pickup" ? r.pickup_time_slot : r.dropoff_time_slot) || "";
  // Rows after the timeslot filter — drives the table, the map and the counter.
  const visibleRows = React.useMemo(
    () => (slotFilter ? rows.filter((r) => rowSlot(r) === slotFilter) : rows),
    [rows, slotFilter]
  );

  // The map shows only ACTIVE stops — a completed leg's pin disappears (WN-0164b).
  const mapRows = React.useMemo(() => visibleRows.filter((r) => !legDone(r)), [visibleRows]);

  // WN-0161: totals shown as text under the "My assigned orders" heading (not a
  // button). Same rules as the printed Collection statement: paid amounts grouped
  // by payment method + total collected, unpaid bucket, and Free-of-Charge count.
  const driverTotals = React.useMemo(() => {
    const byPay = {}; let collected = 0, unpaidTotal = 0, unpaidCount = 0, focCount = 0;
    for (const r of visibleRows) {
      const amt = numTotal(r.order_total);
      const isComp = r.order_total === "F.O.C" || String(r.order?.complemantary_reason || "").trim() !== "";
      const isComplete = Number(r.order?.order_status_id) === 5;
      const isUnpaid = Number(r.order?.unpaid) === 1;
      if (isComp) { focCount++; }
      else if (isComplete || !isUnpaid) { const p = payLabel(r.payment); byPay[p] = (byPay[p] || 0) + amt; collected += amt; }
      else { unpaidTotal += amt; unpaidCount++; }
    }
    return { byPay, collected, unpaidTotal, unpaidCount, focCount };
  }, [visibleRows]);

  // Live "Total unpaid invoices" per customer (same value as the customer page),
  // fetched fresh so it stays accurate even if another system updates the DB.
  const customerIds = React.useMemo(
    () => [...new Set(rows.map((r) => r.order?.customer_id).filter(Boolean))],
    [rows]
  );
  const { data: unpaidByCustomer = {} } = useCustomersUnpaidBulkQuery(customerIds);

  // Persist the current sequence (per-row sort_order) to the backend.
  const persistOrder = React.useCallback((ordered) => {
    const items = ordered.map((a, i) => ({ order_id: a.order_id, type: a.type, sort_order: i + 1 }));
    const body = { items };
    if (!isDriver && driverId) body.driver_id = driverId;
    api.put("/assignments/my-orders/sort", body).catch(() => {});
  }, [isDriver, driverId]);

  // Move a row to position `toPos` (1-based, in the displayed order); everything
  // else shifts. Renumbers the whole list and saves the new order.
  const moveRowByKey = React.useCallback((orderId, kind, toPos) => {
    const visible = rows.map((r) => ({ order_id: r.order_id, type: r.kind }));
    const from = visible.findIndex((v) => v.order_id === orderId && v.type === kind);
    if (from < 0) return;
    let pos = parseInt(toPos, 10);
    if (!Number.isFinite(pos)) return;
    pos = Math.max(1, Math.min(visible.length, pos));
    if (pos - 1 === from) return;
    const [item] = visible.splice(from, 1);
    visible.splice(pos - 1, 0, item);
    setAssignments((prev) => {
      const seen = new Set(visible.map((v) => v.order_id + "|" + v.type));
      const extras = (prev || []).filter((a) => !seen.has(a.order_id + "|" + a.type));
      const next = [...visible, ...extras];
      persistOrder(next);
      return next;
    });
  }, [rows, persistOrder]);

  const driverLabel = isDriver
    ? (user?.username || `${user?.firstname || ""} ${user?.lastname || ""}`.trim() || "Driver")
    : (drivers.find((d) => String(d.driver_id) === String(driverId))?.name || "—");

  // Collection statement (كشف تحصيل): one row per order + totals grouped by
  // payment method and a separate unpaid total, printed for the driver/date.
  const printStatement = (scrollToTotals = false) => {
    if (!visibleRows.length) return;
    const byPay = {};                 // COLLECTED (paid) amounts by payment method
    let collected = 0, unpaidTotal = 0, unpaidCount = 0, focCount = 0;
    const bodyRows = visibleRows.map((r, i) => {
      const amt = numTotal(r.order_total);                 // 0 for Free of Charge
      const pay = payLabel(r.payment);
      const isComp = r.order_total === "F.O.C" || String(r.order?.complemantary_reason || "").trim() !== "";
      const isComplete = Number(r.order?.order_status_id) === 5;
      const isUnpaid = Number(r.order?.unpaid) === 1;
      const orderStatus = r.status_text || "—";            // current order status
      const slot = (legOf(r) === "pickup" ? r.pickup_time_slot : r.dropoff_time_slot) || "";

      let payState, cls;
      if (isComp) {
        // Complementary → Free of Charge, excluded from every total.
        payState = "Free of Charge"; cls = "foc"; focCount++;
      } else if (isComplete || !isUnpaid) {
        // Delivered/paid (Complete counts as paid even if the COD flag lingers).
        payState = isComplete ? "Paid (Complete)" : "Paid"; cls = "paid";
        byPay[pay] = (byPay[pay] || 0) + amt; collected += amt;
      } else {
        // Not collected yet → goes to the Unpaid bucket, NOT the cash/card totals.
        payState = "Unpaid"; cls = "unpaid"; unpaidTotal += amt; unpaidCount++;
      }
      return `<tr>
        <td>${i + 1}</td>
        <td>${r.order_id}</td>
        <td>${esc(r.customer || "")}</td>
        <td>${esc(orderStatus)}</td>
        <td>${esc(r.area || "")}</td>
        <td>${esc(slot)}</td>
        <td>${esc(pay)}</td>
        <td class="num">${amt.toFixed(2)}</td>
        <td class="${cls}">${payState}</td>
      </tr>`;
    }).join("");
    const payRows = Object.entries(byPay)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => `<tr><td>${esc(k)}</td><td class="num">${v.toFixed(2)} JOD</td></tr>`)
      .join("");
    const title = `Collection Statement — ${esc(driverLabel)}`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
      <style>
        *{font-family:'Segoe UI',Tahoma,Arial,sans-serif;box-sizing:border-box}
        body{margin:24px;color:#111}
        h1{font-size:20px;margin:0 0 2px} .sub{color:#555;font-size:13px;margin-bottom:14px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #ccc;padding:5px 7px;text-align:left}
        th{background:#f3f4f6}
        .num{text-align:right;font-variant-numeric:tabular-nums}
        .unpaid{color:#b91c1c;font-weight:700}
        .paid{color:#16a34a;font-weight:600}
        .foc{color:#6b7280;font-weight:700}
        .totals{margin-top:18px;max-width:420px} .totals h3{font-size:14px;margin:0 0 6px}
        .totals td{border:1px solid #ddd}
        .totals .unpaid td{color:#b91c1c}
        .totals .foc td{color:#6b7280}
        .grand{font-size:15px;font-weight:800;margin-top:10px}
        @media print{body{margin:10mm}}
      </style></head><body>
      <h1>${title}</h1>
      <div class="sub">
        Date: <b>${esc(date || "All assigned")}</b> &nbsp;·&nbsp;
        Tasks: <b>${type === "both" ? "Pickup + Delivery" : type === "pickup" ? "Pickup" : "Delivery"}</b> &nbsp;·&nbsp;
        Orders: <b>${visibleRows.length}</b> &nbsp;·&nbsp; Printed: ${new Date().toLocaleString()}
      </div>
      <table><thead><tr>
        <th>#</th><th>Order</th><th>Customer</th><th>Current Order Status</th><th>Area</th><th>Timeslot</th><th>Payment</th><th class="num">Amount</th><th>Status</th>
      </tr></thead><tbody>${bodyRows}</tbody></table>
      <div class="totals" id="totals">
        <h3>Collected by payment method</h3>
        <table><tbody>${payRows}
          <tr><td><b>Total collected</b></td><td class="num"><b>${collected.toFixed(2)} JOD</b></td></tr>
          <tr class="unpaid"><td>Unpaid — NOT collected${unpaidCount ? ` (${unpaidCount} orders)` : ""}</td><td class="num">${unpaidTotal.toFixed(2)} JOD</td></tr>
          ${focCount ? `<tr class="foc"><td>Free of Charge (${focCount} orders)</td><td class="num">0.00 JOD</td></tr>` : ""}
        </tbody></table>
      </div>
      <div class="grand">Total collected: ${collected.toFixed(2)} JOD &nbsp;·&nbsp; Outstanding (unpaid): ${unpaidTotal.toFixed(2)} JOD</div>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Please allow pop-ups to print the statement."); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    if (scrollToTotals) {
      // "Totals" shortcut (WN-0161): open the statement scrolled to the Totals
      // section instead of straight to the print dialog.
      setTimeout(() => { try { w.location.hash = "#totals"; } catch (e) {} }, 300);
    } else {
      setTimeout(() => w.print(), 400);
    }
  };

  return (
    <div className="oc-content">
      {/* WN-0161 (mobile): the toolbar used a fixed single row that overflowed
          off-screen on phones, hiding the filters. It now wraps, and on narrow
          screens every control goes full-width so all filters stay reachable. */}
      <style>{`
        .driver-header { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; }
        .driver-toolbar { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
        .driver-toolbar > * { flex:0 1 auto; }
        /* Wide orders table stays reachable on phones via horizontal scroll. */
        .oc-table-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
        @media (max-width: 768px) {
          .driver-header { align-items:stretch; }
          .driver-header > div:first-child h1 { font-size:20px; margin:0; }
          .driver-toolbar { width:100%; }
          .driver-toolbar > select,
          .driver-toolbar > input,
          .driver-toolbar > button { flex:1 1 100%; min-width:0; width:auto !important; }
        }
      `}</style>
      <div className="oc-header driver-header">
        <div>
          <h1>{isDriver ? "Driver — My Orders" : "Driver Orders"}</h1>
          <nav className="oc-bc"><span>Home</span><span className="sep">›</span><span>Get Orders on Map</span><span className="sep">›</span><span>Driver</span></nav>
        </div>
        <div className="driver-toolbar">
          {!isDriver && (
            <select className="oc-input" value={driverId} onChange={(e) => setDriverId(e.target.value)} style={{ width: 200 }}>
              <option value="">— choose driver —</option>
              {drivers.map((d) => <option key={d.driver_id} value={d.driver_id}>{d.name}</option>)}
            </select>
          )}
          <select className="oc-input" value={type} onChange={(e) => setType(e.target.value)} style={{ width: 180 }}>
            <option value="both">Both (pickup + delivery)</option>
            <option value="dropoff">Delivery (drop-off) only</option>
            <option value="pickup">Pickup only</option>
          </select>
          <select
            className="oc-input"
            value={slotFilter}
            onChange={(e) => setSlotFilter(e.target.value)}
            title="Filter by timeslot"
            style={{ width: 170 }}
          >
            <option value="">All timeslots</option>
            {slotOptions.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <input
            type="date"
            className="oc-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            title="Filter by date (pickup date for pickups, delivery date for deliveries)"
            style={{ width: 160 }}
          />
          <button className="oc-btn" onClick={() => setDate(ammanToday)} title="Show today's orders">Today</button>
          {date && (
            <button className="oc-btn" onClick={() => setDate("")} title="Show all dates">✕</button>
          )}
          <button className="oc-btn" onClick={loadMine}>Refresh</button>
          <button
            className="oc-btn"
            onClick={() => printStatement(false)}
            disabled={!visibleRows.length}
            style={{ background: "#16a34a", color: "#fff" }}
            title="Export a collection statement (totals by payment method + unpaid)"
          >
            🧾 Collection statement
          </button>
        </div>
      </div>

      {err && <div className="oc-card" style={{ color: "#c00" }}>{err}</div>}

      <div className="oc-card" style={{ marginBottom: 16 }}>
        <DriverMap rows={mapRows} onSetPosition={moveRowByKey} />
      </div>

      <div className="oc-card">
        <div className="oc-header">
          <h2>My assigned orders ({visibleRows.length})</h2>
          {/* WN-0161: totals shown here as titles under the heading (not a button). */}
          {visibleRows.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 18px", fontSize: 13, color: "#374151", marginTop: 6 }}>
              {Object.entries(driverTotals.byPay).map(([k, v]) => (
                <span key={k}><b>{k}:</b> {v.toFixed(2)} JOD</span>
              ))}
              <span style={{ color: "#16a34a" }}><b>Total collected:</b> {driverTotals.collected.toFixed(2)} JOD</span>
              <span style={{ color: "#b91c1c" }}>
                <b>Unpaid:</b> {driverTotals.unpaidTotal.toFixed(2)} JOD{driverTotals.unpaidCount ? ` (${driverTotals.unpaidCount})` : ""}
              </span>
              {driverTotals.focCount ? (
                <span style={{ color: "#6b7280" }}><b>Free of Charge:</b> {driverTotals.focCount}</span>
              ) : null}
            </div>
          )}
        </div>
        <div className="oc-table-wrap">
          <table className="oc-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th style={{ width: 90 }}>Task</th>
                <th style={{ width: 90 }}>Order ID</th>
                <th>Customer</th>
                <th>Phone</th>
                <th>Next Status</th>
                <th>Timeslot</th>
                <th>Type</th>
                <th>Payment</th>
                <th style={{ width: 130 }}>Total unpaid invoices</th>
                <th style={{ width: 110 }}>Order Total</th>
                <th>1st</th>
                <th>Area</th>
                <th style={{ width: 70 }}>Map</th>
                <th style={{ width: 220 }}>Customer &amp; Order Notes</th>
                <th style={{ width: 90 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {(loadingIds || isFetching) && visibleRows.length === 0 ? (
                <tr><td colSpan={16} style={{ textAlign: "center", padding: 24 }}>Loading…</td></tr>
              ) : visibleRows.length === 0 ? (
                <tr><td colSpan={16} style={{ textAlign: "center", padding: 24 }}>
                  {!isDriver && !driverId ? "Choose a driver above to see their assigned orders." : "No orders assigned yet."}
                </td></tr>
              ) : (
                visibleRows.map((r, i) => {
                  const done = legDone(r);
                  return (
                  <tr key={`${r.order_id}-${r.kind}`} style={done ? { opacity: 0.55, background: "#f3f4f6" } : undefined}>
                    <td>
                      <input
                        type="number"
                        min="1"
                        className="oc-input"
                        key={`pos-${r.order_id}-${r.kind}-${i}`}
                        defaultValue={i + 1}
                        title="Change the stop number — the row moves there and the others shift"
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                        onFocus={(e) => e.currentTarget.select()}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v !== "" && Number(v) !== i + 1) moveRowByKey(r.order_id, r.kind, v);
                        }}
                        style={{ width: 52, textAlign: "center", padding: "2px 4px" }}
                      />
                    </td>
                    <td>
                      {done ? (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 10, color: "#374151", background: "#d1d5db" }}>
                          {doneLabel(r)}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 10, color: "#fff", background: legOf(r) === "pickup" ? "#f59e0b" : "#2563eb" }}>
                          {legOf(r) === "pickup" ? "Pickup" : "Delivery"}
                        </span>
                      )}
                    </td>
                    <td><Link to={`/orders/${r.order_id}`} target="_blank">{r.order_id}</Link></td>
                    <td dir="auto">
                      {r.order?.customer_id ? (
                        <Link to={`/customers/${r.order.customer_id}`} target="_blank">{r.customer}</Link>
                      ) : (r.customer)}
                    </td>
                    <td>{phoneOf(r) ? <a href={`tel:${phoneOf(r)}`} dir="ltr">{phoneOf(r)}</a> : "—"}</td>
                    <td>{r.next_status_text || r.status_text || "—"}</td>
                    <td>{(legOf(r) === "pickup" ? r.pickup_time_slot : r.dropoff_time_slot) || "—"}</td>
                    <td>{r.type || "—"}</td>
                    <td>
                      {(() => {
                        const cur = payOverride[r.order_id] ?? r.payment ?? "";
                        const curCode = paymentInfo(cur).code;
                        const isComplete = Number(r.order?.order_status_id) === 5;
                        const known = PAYMENT_METHODS.some((m) => m.code === curCode);
                        if (isComplete) return <span title="Order complete — locked">{paymentLabel(cur)}</span>;
                        return (
                          <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                            <select
                              className="oc-input"
                              value={curCode}
                              disabled={paySaving === r.order_id}
                              onChange={(e) => savePayment(r, e.target.value)}
                              style={{ padding: "2px 4px", maxWidth: 170 }}
                              title="Change payment method (saved instantly, logged)"
                            >
                              {!known && cur ? <option value={curCode}>{paymentLabel(cur)}</option> : null}
                              {PAYMENT_METHODS.map((m) => (
                                <option key={m.code} value={m.code}>{m.emoji} {m.label}</option>
                              ))}
                            </select>
                            {paySaving === r.order_id ? <span style={{ fontSize: 11 }}>…</span> : null}
                            {paySaved === r.order_id ? <span style={{ color: "#16a34a", fontSize: 12 }}>✓</span> : null}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ fontWeight: 700, color: "#dc2626" }}>
                      {(() => {
                        const amt = Number(unpaidByCustomer[r.order?.customer_id] ?? 0);
                        if (amt > 0) {
                          return (
                            <button
                              type="button"
                              onClick={() => openCollect(r)}
                              title="Collect this customer's unpaid balance"
                              style={{ background: "none", border: "none", color: "#dc2626", fontWeight: 700, cursor: "pointer", padding: 0 }}
                            >
                              💰 {amt.toFixed(2)}
                            </button>
                          );
                        }
                        return amt.toFixed(2);
                      })()}
                    </td>
                    <td>
                      {isFoc(r) ? (
                        <b style={{ color: "#6b7280" }}>Free of Charge</b>
                      ) : editCollected === r.order_id ? (
                        <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                          <input
                            type="number" min="0" className="oc-input" autoFocus
                            value={collectedVal}
                            placeholder={fmtTotal(r.order_total)}
                            onChange={(e) => setCollectedVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveCollected(r); if (e.key === "Escape") setEditCollected(null); }}
                            style={{ width: 68, padding: "2px 4px" }}
                          />
                          <button className="oc-btn" title="Save" onClick={() => saveCollected(r)}>✓</button>
                          <button className="oc-btn" title="Cancel" onClick={() => setEditCollected(null)}>✕</button>
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                          {fmtTotal(r.order_total)}
                          {r.order?.driver_collected != null &&
                            Number(r.order.driver_collected) !== Number(r.order_total) && (
                              <span style={{ color: "#2563eb", fontSize: 11 }} title="Collected">
                                → {Number(r.order.driver_collected).toFixed(2)}
                              </span>
                            )}
                          <button
                            className="oc-btn"
                            title="Enter collected amount (if paid more/less)"
                            onClick={() => { setEditCollected(r.order_id); setCollectedVal(r.order?.driver_collected != null ? String(r.order.driver_collected) : ""); }}
                            style={{ fontSize: 11, padding: "1px 6px" }}
                          >✏️</button>
                        </span>
                      )}
                    </td>
                    <td>{Number(r.first_order) === 1 ? "1st" : ""}</td>
                    <td>{r.area || "—"}</td>
                    <td>
                      {mapUrlOf(r)
                        ? <a href={mapUrlOf(r)} target="_blank" rel="noreferrer" title="Open in Google Maps">📍 Map</a>
                        : "—"}
                    </td>
                    <td dir="auto" style={{ fontSize: 12 }}>{stripHtml(r.notes_display)}</td>
                    <td>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        <Link className="oc-btn" to={`/orders/${r.order_id}`} target="_blank">Open</Link>
                        {r.next_order_status_id && !done ? (
                          <button
                            className="oc-btn"
                            style={{ background: "#2563eb", color: "#fff" }}
                            disabled={advancing === r.order_id}
                            onClick={() => advanceStatus(r)}
                            title={`Advance to ${r.next_status_text || "next status"}`}
                          >
                            {advancing === r.order_id ? "…" : `Next → ${r.next_status_text || ""}`}
                          </button>
                        ) : null}
                        {(() => {
                          const n = attachOverride[r.order_id] ?? r.attachment_count ?? 0;
                          return (
                            <label
                              className="oc-btn"
                              style={{ cursor: "pointer" }}
                              title="Attach a payment-proof / Cliq screenshot for this order"
                            >
                              {uploadingImg === r.order_id ? "…" : `📎 Attach${n ? ` (${n})` : ""}`}
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/heic,image/heif,image/*"
                                capture="environment"
                                style={{ display: "none" }}
                                disabled={uploadingImg === r.order_id}
                                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; attachImage(r, f); }}
                              />
                            </label>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {collectRow && (
        <div
          onClick={() => !collectBusy && setCollectRow(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 12, width: "min(680px, 94vw)", maxHeight: "90vh", overflow: "auto", padding: 20, boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h2 style={{ margin: 0 }}>💰 Collect Payment</h2>
              <button className="oc-btn" onClick={() => !collectBusy && setCollectRow(null)}>✕</button>
            </div>
            <div style={{ color: "#374151", marginBottom: 12 }}>
              <b dir="auto">{collectRow.customer}</b>
              {phoneOf(collectRow) ? <> · <a href={`tel:${phoneOf(collectRow)}`} dir="ltr">{phoneOf(collectRow)}</a></> : null}
            </div>

            {!collectList ? (
              <div style={{ padding: 20 }}>Loading unpaid orders…</div>
            ) : collectList.orders.length === 0 ? (
              <div style={{ padding: 20 }}>No unpaid orders for this customer.</div>
            ) : (
              <>
                <div className="oc-table-wrap" style={{ maxHeight: 300, overflow: "auto" }}>
                  <table className="oc-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>
                          <input
                            type="checkbox"
                            checked={collectSel.size === collectList.orders.length}
                            ref={(el) => { if (el) el.indeterminate = collectSel.size > 0 && collectSel.size < collectList.orders.length; }}
                            onChange={() => setCollectSel((prev) => prev.size === collectList.orders.length ? new Set() : new Set(collectList.orders.map((o) => o.order_id)))}
                          />
                        </th>
                        <th>Order</th><th>Date</th><th>Type</th><th>Status</th><th style={{ textAlign: "right" }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collectList.orders.map((o) => (
                        <tr key={o.order_id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={collectSel.has(o.order_id)}
                              onChange={() => setCollectSel((prev) => { const n = new Set(prev); n.has(o.order_id) ? n.delete(o.order_id) : n.add(o.order_id); return n; })}
                            />
                          </td>
                          <td>{o.order_id}</td>
                          <td>{String(o.date_added || "").slice(0, 10)}</td>
                          <td>{o.order_type}</td>
                          <td>{o.status_name}</td>
                          <td style={{ textAlign: "right" }}>{Number(o.total || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", marginTop: 14 }}>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>
                    Selected: {collectSel.size} · {collectTotal.toFixed(2)} JOD
                  </div>
                  <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                    Method:
                    <select className="oc-input" value={collectMethod} onChange={(e) => setCollectMethod(e.target.value)}>
                      {PAYMENT_METHODS.filter((m) => m.code !== "free_checkout").map((m) => (
                        <option key={m.code} value={m.code}>{m.emoji} {m.label}</option>
                      ))}
                    </select>
                  </label>
                  <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                    {collectMethod === "cliq" ? "Transfer screenshot (required):" : "Proof image (optional):"}
                    <input type="file" accept="image/*" capture="environment" onChange={(e) => setCollectFile(e.target.files?.[0] || null)} />
                  </label>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                  <button className="oc-btn" onClick={() => !collectBusy && setCollectRow(null)} disabled={collectBusy}>Cancel</button>
                  <button
                    className="oc-btn"
                    style={{ background: "#16a34a", color: "#fff" }}
                    onClick={confirmCollect}
                    disabled={collectBusy || collectSel.size === 0}
                  >
                    {collectBusy ? "Collecting…" : `Confirm — ${collectTotal.toFixed(2)} JOD`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
